import { type ReactNode, useCallback, useMemo } from 'react';

import {
  Avatar,
  Box,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

export type ActivityNextStepAction =
  | 'schedule_call'
  | 'schedule_visit'
  | 'schedule_task'
  | 'lead_lost'
  | 'lead_won'
  | 'note'
  | 'do_nothing';

export type ActivityNextStepsDialogOption<
  TAction extends ActivityNextStepAction = ActivityNextStepAction,
> = {
  key: TAction;
  label: string;
  icon: ReactNode;
  avatarColor: string;
  description?: string;
  disabled?: boolean;
};

type ActivityNextStepsDialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  options?: ActivityNextStepsDialogOption[];
  onSelect: (key: ActivityNextStepAction) => void;
  subtitle?: string;
};

export const ActivityNextStepsDialog = ({
  open,
  title,
  onClose,
  options = [],
  onSelect,
  subtitle,
}: ActivityNextStepsDialogProps) => {
  const { t } = useLocale();

  const handleSelect = useCallback(
    (action: ActivityNextStepAction) => {
      onSelect(action);
      onClose();
    },
    [onSelect, onClose],
  );

  const resolvedSubtitle = useMemo(() => {
    if (subtitle) {
      return subtitle;
    }

    return t('What would you like to do next?');
  }, [subtitle, t]);

  return (
    <SlideDialog open={open} onClose={onClose} title={title} maxWidth="xs">
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body1">{resolvedSubtitle}</Typography>
        <List disablePadding>
          {options.map(option => (
            <ListItemButton
              key={option.key}
              onClick={() => {
                if (!option.disabled) {
                  handleSelect(option.key);
                }
              }}
              sx={{ px: 0, '&:hover': { backgroundColor: 'transparent' } }}
              disabled={option.disabled}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: option.avatarColor,
                    color: theme => theme.palette.common.white,
                  }}
                >
                  {option.icon}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={option.label}
                secondary={option.description}
                slotProps={{
                  primary: { sx: { fontWeight: 500 } },
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </SlideDialog>
  );
};
